import React from 'react';
import PropTypes from 'prop-types';

import { Card, Image, Icon, Popup, Button, Table, Label, Dropdown, Transition } from 'semantic-ui-react';
import _ from 'lodash';
import moment from 'moment';
import TargetLink from '../target-link';

import { scientificFormat } from '../../utils/helpers/formatting';

const PLATFORMS = {
  twitter: {
    color: 'blue',
    name: 'twitter',
  },
  instagram: {
    color: 'pink',
    name: 'instagram',
  },
  facebook: {
    color: 'pink',
    name: 'facebook',
  },
  youtube: {
    color: 'red',
    name: 'youtube',
  },
};

const imageStyleSmall = {
  height: '180px',
  width: '180px',
  objectFit: 'cover',
  display: 'block',
  margin: '0px auto',
};

const imageStyleLarge = {
  height: '300px',
  width: '306px',
  objectFit: 'cover',
  display: 'block',
  margin: '0px auto',
};

const divStyleSmall = {
  height: '150px',
  padding: '15px',
  overflow: 'hidden',
};

const divStyleLarge = {
  height: '300px',
  padding: '15px',
  overflow: 'hidden',
};

const overflowEllipsis = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const STORY_IMAGE = 'https://s3-eu-west-1.amazonaws.com/dyzio-static-files/images/instagram-story2.png';

const STYLES = {
  fontSizeLarge: {
    fontSize: '100%',
  },
  fontSizeSmall: {
    fontSize: '90%',
  },
  empty: {},
  noPaddingBottom: {
    padding: 10,
  },
  noPadding: {
    padding: 0,
  },
  compactVertical: {
    padding: '5px 14px',
  },
  noVerticalPadding: {
    padding: '0px 14px',
  },
  displayBlock: {
    display: 'block',
  },
  fontGreen: {
    color: '#20c49e',
    fontSize: '90%',
    lineHeight: '12px',
  },
  fontGrey: {
    color: 'grey',
    fontSize: '90%',
    lineHeight: '12px',
  },
};

const smallText = {
  fontSize: '90%',
  lineHeight: '12px',
};

const DIVIDER = 'DIVIDER';

const LargeCardHeader = ({ data, displayName, lightAsset, hamburgerOptions }) => (
  <Table basic="very" singleLine verticalAlign="middle">
    <Table.Body>
      <Table.Row style={smallText}>
        <Table.Cell collapsing style={{ padding: 0, paddingRight: '5px' }}>
          <Image
            src={data.profilePic}
            className="circular"
            size="mini"
          />
        </Table.Cell>
        <Table.Cell style={{ padding: 0, paddingRight: '5px' }}>
          <div style={overflowEllipsis}>
            <div style={{ color: '#000', fontSize: '90%', lineHeight: '14px' }}>
              <b>{displayName}</b>
              &nbsp;{ data.isVerified && <Icon name="check circle" color="blue" size="small" /> }
            </div>
            <div style={{ fontSize: '80%', lineHeight: '14px'  }}>
              <TargetLink
                renderIf
                href={data.platformPage}
              >
                @{data.handle}
              </TargetLink>
            </div>
          </div>
        </Table.Cell>
        <Table.Cell collapsing style={{ padding: 0 }}>
          <Dropdown
            icon={null}
            pointing="top right"
            floating
            trigger={<Button inverted icon="bars" color="pink" size="mini" />}
          >
            <Dropdown.Menu>
              {
                _.map(hamburgerOptions, opt => {
                  if (opt.type === DIVIDER) {
                    return (<Dropdown.Divider />);
                  }

                  if (opt.disabled && opt.popupText) {
                    return (
                      <Popup
                        trigger={
                          <Dropdown.Item
                            onClick={_.noop()}
                            value={opt.value}
                          >
                            <Icon name={opt.icon} />{opt.label}
                          </Dropdown.Item>
                        }
                        content={opt.popupText}
                      />
                    );
                  }

                  return (
                    <Dropdown.Item
                      disabled={opt.disabled}
                      onClick={() => opt.onClick(lightAsset)}
                      value={opt.value}
                    >
                      <Icon name={opt.icon} />{opt.label}
                    </Dropdown.Item>
                  );
                })
              }
            </Dropdown.Menu>
          </Dropdown>
        </Table.Cell>
      </Table.Row>
    </Table.Body>
  </Table>
);

const SmallCardHeader = ({ displayName, data }) => (
  <div style={{ color: '#000' }}>
    <b>{displayName}</b>
    &nbsp;{ data.isVerified && <Icon name="check circle" color="blue" size="small" /> }
  </div>
);

const hasSupportingData = asset => {
  let hasData = false;
  if (asset.localState && asset.localState.supportingFileAdded) {
    hasData = true;
  }
  if (asset.relationships.supportingData.data.length > 0) hasData = true;
  return hasData;
};

const isAssetVisible = asset => {
  let isVisible = true;
  if (asset.localState && asset.localState.isAssetDeleted) {
    isVisible = false;
  }
  return isVisible;
};

const BrokenAssetCard = ({ attributes }) => (
  <Card>
    <Card.Content>
      <Card.Header>
        <Icon name="ban" color="red" />Broken Asset
      </Card.Header>
    </Card.Content>
    <Card.Content>
      <Icon name="ban" color="red" size="huge" />
    </Card.Content>
    <Card.Content style={overflowEllipsis}>
      <pre>{attributes.assetURL}</pre>
    </Card.Content>
  </Card>
);

const AssetCard = ({ asset, viewMode, hamburgerOptions }) => {
  let data = {};
  const atts = asset.attributes;
  if (atts.assetType === 'STORY') {
    data = {
      profilePic: STORY_IMAGE,
      text: atts.extraData.text,
      engagements: atts.extraData.opens,
      mainImageSrc: STORY_IMAGE,
      displayName: atts.discussionName,
    };
  } else {
    if (!atts.initialData) {
      return <BrokenAssetCard attributes={atts} />;
    }

    data = atts.initialData.standardFormat;
  }

  data.extraData = atts.extraData;
  data.hasSupportingData = hasSupportingData(asset);
  data.isAssetVisible = isAssetVisible(asset);

  const lightAsset = {
    type: asset.type,
    id: asset.id,
    assetId: asset.attributes.assetId,
    assetURL: asset.attributes.assetURL,
    platform: asset.attributes.platform,
    assetType: asset.attributes.assetType,
  };

  const platform = PLATFORMS[_.lowerCase(asset.attributes.platform)];
  const largeCard = (viewMode === 'cards.large');
  const imageStyle = largeCard ? imageStyleLarge : imageStyleSmall;
  const divStyle = largeCard ? divStyleLarge : divStyleSmall;

  const displayName = (viewMode === 'cards.large')
    ? data.displayName
    : _.truncate(data.displayName, { length: 22, separator: '' });

  return (
    <Transition visible={data.isAssetVisible} animation="scale" duration={500} unmountOnHide>

      <Card color={platform.color} style={STYLES.fontSizeSmall} key={asset.id}>
        <Card.Content style={largeCard ? STYLES.noPaddingBottom : STYLES.empty}>
          <Card.Header style={largeCard ? STYLES.fontSizeLarge : STYLES.fontSizeSmall}>
            { largeCard
              ? <LargeCardHeader
                data={data}
                lightAsset={lightAsset}
                hamburgerOptions={hamburgerOptions}
                displayName={displayName}
              />
              : <SmallCardHeader data={data} displayName={displayName} />
            }
          </Card.Header>
        </Card.Content>
        <Card.Content style={STYLES.noPadding}>

          <TargetLink
            renderIf
            href={data.assetURL}
          >
            {
              data.mainImageSrc
              ?
                <Image
                  src={data.mainImageSrc}
                  style={imageStyle}
                />
              : <div style={divStyle}>{data.text}</div>
            }
          </TargetLink>
        </Card.Content>

        { largeCard &&
          <Card.Content style={STYLES.compactVertical}>
            <Popup
              trigger={<span style={{ fontSize: '90%' }}>{ _.truncate(data.text, { length: 38, separator: '' }) }</span>}
              content={data.text}
            />
          </Card.Content>
        }

        { !largeCard &&
          <Card.Content extra style={STYLES.compactVertical}>
            <div style={overflowEllipsis}>
              <Icon name={platform.name} color={platform.color} /> {scientificFormat(data.engagements)} engagements
            </div>
          </Card.Content>
        }

        { largeCard &&
          <Card.Content style={STYLES.compactVertical}>
            <Table basic="very" singleLine fixed>
              <Table.Body>
                <Table.Row style={smallText}>
                  <Table.Cell style={{ padding: 0, paddingRight: '5px' }}>
                    <Label basic style={STYLES.displayBlock}>
                      <Icon name="thumbs outline up" /> {scientificFormat(data.likes)}
                    </Label>
                  </Table.Cell>
                  <Table.Cell style={{ padding: 0, paddingRight: '5px' }}>
                    <Label basic style={STYLES.displayBlock}>
                      <Icon name="comments outline" /> {scientificFormat(data.comments)}
                    </Label>
                  </Table.Cell>
                  <Table.Cell style={{ padding: 0 }}>
                    <Label basic style={STYLES.displayBlock}>
                      <Icon name="share" /> {scientificFormat(data.shares)}
                    </Label>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Card.Content>
        }

        { largeCard &&
          <Card.Content textAlign="center">

            <span style={data.hasSupportingData ? STYLES.fontGreen : STYLES.fontGrey}>
              <Icon name="upload" />
              this indicates manual data
            </span>
          </Card.Content>
        }

        { largeCard &&
          <Card.Content style={STYLES.compactVertical} extra>
            <Icon name="calendar" />&nbsp;{moment(data.publishedDate).format('Do MMM YYYY')}
          </Card.Content>
        }

      </Card>
    </Transition>

  );
};

AssetCard.propTypes = {
  asset: PropTypes.object.isRequired,
  viewMode: PropTypes.string.isRequired,
};

export default AssetCard;



/** WEBPACK FOOTER **
 ** ./src/components/asset-card/index.js
 **/